// @ts-strict-ignore
import sharedConstants from "@lib/shared_constants";

import { LIQUID_REGEXES } from "./constants";

const { LIQUID } = sharedConstants;
const NL = "\\r?\\n";
const LIQUID_TAG_START_WITH_LINEBREAK = new RegExp(`${NL}${LIQUID_REGEXES.TAG_START.source}`, "g");
const LIQUID_TAG_END_WITH_LINEBREAK = new RegExp(`${LIQUID_REGEXES.TAG_END.source}${NL}`, "g");
const LIQUID_TAG_WITH_LINEBREAK_AT_START_OF_STRING = new RegExp(`^${LIQUID_TAG_START_WITH_LINEBREAK.source}`, "g");
const LIQUID_TAG_WITH_LINEBREAK_AT_END_OF_STRING = new RegExp(`${LIQUID_TAG_END_WITH_LINEBREAK.source}$`, "g");
const DOUBLE_LINEBREAK_BETWEEN_LIQUID_TAGS = new RegExp(
  `${LIQUID_TAG_END_WITH_LINEBREAK.source}${LIQUID_TAG_START_WITH_LINEBREAK.source}`,
  "g"
);

const CONTENT_BETWEEN_LIQUID_TAGS_AND_OPERATORS = new RegExp(
  // Non-capture the start of a Tag
  `(?:${LIQUID_REGEXES.TAG_START.source})` +
    // Capture anything except for %
    `([^%]*)` +
    // End of Tag
    `(?:${LIQUID_REGEXES.TAG_END.source})` +
    // Match the above expression, OR
    `|` +
    // Non-capture the start of an Object
    `(?:${LIQUID_REGEXES.OBJECT_START.source})` +
    // Per the Liquid grammar, it's invalid Liquid syntax to include a `}` inside of an Object
    // we capture everything else
    `([^}]*)` +
    // Non-capture the end of the object
    `(?:${LIQUID_REGEXES.OBJECT_END.source})`,
  // Global and multi-line flags so we return multiple matches across all lines of text
  "gm"
);

const hasCustomTag = (messageString: string): boolean => {
  if (!messageString) {
    return false;
  }
  return messageString.match(LIQUID_REGEXES.TAG)?.length > 0;
};

const hasLiquid = (messageString: string): boolean => {
  if (!messageString) {
    return false;
  }
  const liquidStrings = messageString.match(LIQUID_REGEXES.TAG_OR_OBJECT) || [];
  return liquidStrings.length > 0;
};

const startsWithLiquid = (messageString: string): boolean => {
  if (!messageString) {
    return false;
  }
  const liquidStrings = messageString.match(`^(${LIQUID.REGEXES.TAG_OR_OBJECT})`) || [];
  return liquidStrings.length > 0;
};

// Check if messageString contains tag tagName
const hasTag = (messageString: string, tagName: string): boolean => {
  if (!messageString) {
    return false;
  }

  // Check if the message contains a tag that starts with the specified tagName
  const tagRegex = new RegExp(`${LIQUID.REGEXES.TAG_START}\\s*${tagName}(?:\\s|${LIQUID.REGEXES.TAG_END})`);
  return tagRegex.test(messageString);
};

const messageExcludingLiquid = (messageString: string): string => {
  return messageString.replace(LIQUID_REGEXES.TAG_OR_OBJECT, "");
};

const lengthExcludingLiquid = (messageString: string): number => {
  if (!messageString) {
    return 0;
  }
  return messageExcludingLiquid(messageString).length;
};

// Remove all newlines that come immediately before or after a liquid tag
const killLiquidLinebreaks = (messageString: string): string => {
  if (messageString === null || typeof messageString === "undefined") {
    return null;
  }
  return messageString.replace(LIQUID_TAG_START_WITH_LINEBREAK, "{%").replace(LIQUID_TAG_END_WITH_LINEBREAK, "%}");
};

// Replaces all double-quotes (including escaped double-quotes) with a single quote.
// If `messageString` does not contain liquid or there are no double-quotes to escape it returns back unchanged
const sanitizeDoubleQuotes = (messageString: string) => {
  if (messageString === null || typeof messageString === "undefined") {
    return "";
  }

  // Gather all of the liquid tags and objects
  const matches = messageString.match(CONTENT_BETWEEN_LIQUID_TAGS_AND_OPERATORS);

  if (!matches) {
    return messageString;
  }

  let sanitizedMessage = messageString;

  // Then replace all double-quotes and escaped double-quotes with a single quote
  matches.forEach((match) => {
    if (match.indexOf('"') !== -1) {
      const sanitized = match.replace(/"|\\"/gm, "'");
      // A replacer function is used (not the string) so `String.prototype.replace`
      // does not interpret `$&`, `$'`, `` $` ``, `$$` patterns in the replacement.
      // A `$` immediately preceding the new `'` (e.g. `{{ "USD$" }}` → `{{ 'USD$' }}`)
      // would otherwise form `$'`, which inserts the post-match substring and corrupts the output.
      sanitizedMessage = sanitizedMessage.replace(match, () => sanitized);
    }
  });

  return sanitizedMessage;
};

// Put newlines at the beginning and end of all conditional statements so that they're formatted in "code style."
// run as a compound regex for easier understandability -- we first restore all linebreaks, then remove the very first
// and last
const restoreLiquidLinebreaks = (messageString: string): string => {
  if (messageString === null || typeof messageString === "undefined") {
    return null;
  }
  // The last two regex replaces here are to catch the fact that we'll put newlines as the very first and very last
  // characters if the entire messages are wrapped in conditionals. Then, we account for the fact that if we have 2
  // conditionals that abut one another directly, we'll have an extra linebreak, so we remove the double linebreak.
  return messageString
    .replace(LIQUID_REGEXES.TAG_END, "%}\n")
    .replace(LIQUID_REGEXES.TAG_START, "\n{%")
    .replace(LIQUID_TAG_WITH_LINEBREAK_AT_START_OF_STRING, "{%")
    .replace(LIQUID_TAG_WITH_LINEBREAK_AT_END_OF_STRING, "%}")
    .replace(DOUBLE_LINEBREAK_BETWEEN_LIQUID_TAGS, "%}\n{%");
};

export const createMessageExtrasLiquid = (key: string, value: string) => {
  return `{% ${LIQUID.NAMESPACES.MESSAGE_EXTRAS} :key ${key} :value ${value} %}`;
};

export const createTranslationTagsLiquid = (id: string) => {
  return `{% ${LIQUID.NAMESPACES.TRANSLATIONS} ${id} %} {% end${LIQUID.NAMESPACES.TRANSLATIONS} %}`;
};

const liquidConditionalSplits = new RegExp(
  LIQUID_REGEXES.TAG_START.source + "\\s*(?:else|elsif.*?|when.*?)\\s*" + LIQUID_REGEXES.TAG_END.source,
  "g"
);

const conditionalMarker = /\{%\s*((end)?(if|case|unless)).*?%\}/g;

/**
 * @summary finds the length of a templated liquid string assuming that all objects are length 0, and we take the longest path for every conditional
 */
export const getLiquidLength = (message: string): number => {
  conditionalMarker.lastIndex = 0;
  let workingString = message.toString();
  let conditionalTag;

  const conditionalStack = [];

  do {
    conditionalTag = conditionalMarker.exec(workingString);
    if (conditionalTag) {
      const [tag, _, isEnd, tagType] = conditionalTag;
      if (isEnd) {
        const lastTag = conditionalStack.length && conditionalStack.pop();
        if (!lastTag || lastTag[3] !== tagType) {
          // in the case where their conditional order is invalid, just omit liquid
          return lengthExcludingLiquid(workingString);
        }
        const substring = workingString.substring(lastTag.index, conditionalTag.index + tag.length);
        const longestConditional = getLongestConditionalBlock(substring);
        workingString = workingString.replace(substring, () => longestConditional);
        // since we're modifying the string, we need to reset the regex
        conditionalMarker.lastIndex = lastTag.index + longestConditional.length;
      } else {
        conditionalStack.push(conditionalTag);
      }
    }
  } while (conditionalTag);
  return lengthExcludingLiquid(workingString);
};

/** getLongestConditional
 * @summary given a conditional block, find the longest block assuming all attributes are length 0
 * @param liquidConditional a string beginning with a conditional tag and ending with a matching end tag
 * @returns the longest conditional in the string, without liquid
 */
const getLongestConditionalBlock = (liquidConditional: string): string => {
  liquidConditionalSplits.lastIndex = 0;
  const withoutLineBreaks = killLiquidLinebreaks(liquidConditional);
  const splitByConditionalTags = withoutLineBreaks.split(liquidConditionalSplits);
  const cleanSplits = splitByConditionalTags.map((split) => {
    return split.replace(LIQUID_REGEXES.TAG_OR_OBJECT, "");
  });
  const longest = cleanSplits.reduce((longest, current) => {
    return current.length > longest.length ? current : longest;
  }, "");
  return longest;
};

export default {
  hasCustomTag,
  hasLiquid,
  startsWithLiquid,
  hasTag,
  killLiquidLinebreaks,
  lengthExcludingLiquid,
  messageExcludingLiquid,
  restoreLiquidLinebreaks,
  createMessageExtrasLiquid,
  createTranslationTagsLiquid,
  sanitizeDoubleQuotes,
};
